
import React, { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';

import {
  BankOutlined,
  ExperimentOutlined,
  TeamOutlined,
  FileTextOutlined,
  RiseOutlined,
  LaptopOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  ReadOutlined,
  UsergroupAddOutlined,
  NotificationOutlined,
  SolutionOutlined,
  RocketOutlined,
  ApartmentOutlined,
  FundOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

import '../../Styles/ImpactSectionGrid.css';

const { Title } = Typography;

/* =========================================
   NCGSA IMPACT STATISTICS
   ========================================= */

const impactStats = [
  {
    value: 6,
    suffix: '',
    label: 'Partner Universities',
    icon: BankOutlined,
  },
  {
    value: 7,
    suffix: '',
    label: 'Specialized Laboratories',
    icon: ExperimentOutlined,
  },
  {
    value: 190,
    suffix: '+',
    label: 'MS/PhD Graduates Achieved',
    icon: TeamOutlined,
  },
  {
    value: 390,
    suffix: '+',
    label: 'Journal/Conference Publications',
    icon: FileTextOutlined,
  },
  {
    value: 730,
    suffix: '+',
    label: 'Impact Factor',
    icon: RiseOutlined,
  },
  {
    value: 120,
    suffix: '+',
    label: 'Software & Hardware Solutions',
    icon: LaptopOutlined,
  },
  {
    value: 450,
    suffix: '+',
    label: 'National Datasets / Geospatial Inventories',
    icon: DatabaseOutlined,
  },
  {
    value: 470,
    suffix: '+',
    label: 'Analytical Models & Thematic Maps',
    icon: GlobalOutlined,
  },
  {
    value: 95,
    suffix: '+',
    label: 'Workshops & Trainings',
    icon: ReadOutlined,
  },
  {
    value: 5600,
    suffix: '+',
    label: 'Professionals Trained',
    icon: UsergroupAddOutlined,
  },
  {
    value: 31000,
    suffix: '+',
    label: 'Space Awareness Outreach (Persons Reached)',
    icon: NotificationOutlined,
  },
  {
    value: 200,
    suffix: '+',
    label: 'Internship & Placement Opportunities',
    icon: SolutionOutlined,
  },
  {
    value: 5,
    suffix: '+',
    label: 'Startups / Commercialized Products',
    icon: RocketOutlined,
  },
  {
    value: 100,
    suffix: '+',
    label: 'Industry/Academia Linkages Achieved',
    icon: ApartmentOutlined,
  },
  {
    value: 13,
    suffix: '',
    label: 'Research Fund Projects',
    icon: FundOutlined,
  },
  {
    value: 10,
    suffix: '',
    label: 'Academic Institutions',
    icon: BankOutlined,
  },
  {
    value: 11,
    suffix: '',
    label: 'Partner Organizations',
    icon: SafetyCertificateOutlined,
  },
  {
    value: 740,
    suffix: '+',
    label: 'Outputs',
    icon: FileDoneOutlined,
  },
];

/* =========================================
   ANIMATED COUNTER
   ========================================= */

const CountUp = ({
  active,
  value,
  duration = 1700,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    let frameId;
    let startTime;

    const animate = (timestamp) => {
      if (startTime === undefined) {
        startTime = timestamp;
      }

      const progress = Math.min(
        (timestamp - startTime) / duration,
        1
      );

      const easedProgress =
        1 - Math.pow(1 - progress, 4);

      setCount(Math.round(value * easedProgress));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate);
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [active, value, duration]);

  return <>{count.toLocaleString('en-US')}</>;
};

/* =========================================
   IMPACT SECTION
   ========================================= */

const ImpactSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return undefined;

    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.12,
      }
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="impact-grid-section"
      aria-labelledby="impact-grid-title"
    >
      <div className="impact-grid-container">

        {/* SECTION HEADING */}

        <div className="impact-grid-heading">

          <p className="impact-grid-eyebrow">
            OUR ACHIEVEMENTS
          </p>

          <Title
            id="impact-grid-title"
            level={2}
            className="impact-grid-title"
          >
            Impacts
          </Title>

          <div
            className="impact-grid-line"
            aria-hidden="true"
          />

          <p className="impact-grid-description">
            Advancing Pakistan's space ecosystem through
            research excellence, innovative technologies,
            academic partnerships, capacity building,
            and industry collaboration.
          </p>

        </div>

        {/* IMPACT CARDS */}

        <div className="impact-grid-stats">

          {impactStats.map(
            ({
              value,
              suffix,
              label,
              icon: Icon,
            }) => (

              <article
                className="impact-grid-card"
                key={label}
              >

                <div
                  className="impact-grid-icon-wrap"
                  aria-hidden="true"
                >
                  <Icon className="impact-grid-icon" />
                </div>

                <div
                  className="impact-grid-number"
                  aria-label={`${value.toLocaleString('en-US')}${suffix} ${label}`}
                >
                  <CountUp
                    active={isVisible}
                    value={value}
                  />

                  <span className="impact-grid-suffix">
                    {suffix}
                  </span>

                </div>

                <p className="impact-grid-label">
                  {label}
                </p>

                <span
                  className="impact-grid-card-accent"
                  aria-hidden="true"
                />

              </article>

            )
          )}

        </div>

      </div>
    </section>
  );
};

export default ImpactSection;
